import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { GenreLabels, LocationLabels, type Genre } from "@mbb/config";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import FormMessage from "../../components/FormMessage";
import { ApiError, apiFetch } from "../../lib/api";

type Detail = {
  id: string;
  fullName: string;
  preferredName: string | null;
  email: string;
  location: keyof typeof LocationLabels;
  photoUrl: string | null;
  currentRead: string | null;
  favoriteGenres: Genre[];
  booksPerMonth: string | null;
  bookSuggestion: string | null;
  whyJoin: string;
  referralSource: string | null;
  instagramHandle: string | null;
  acknowledgedFee: boolean;
  agreedToGuidelines: boolean;
  createdAt: string;
  reviewedAt: string | null;
  reviewedBy: { id: string; fullName: string; email: string } | null;
  user: {
    id: string;
    email: string;
    status: "PENDING" | "REJECTED" | "APPROVED_UNPAID" | "ACTIVE" | "LAPSED";
    role: "MEMBER" | "ADMIN";
  };
};

const STATUS_LABELS: Record<Detail["user"]["status"], string> = {
  PENDING: "Pending review",
  APPROVED_UNPAID: "Approved · invite sent",
  ACTIVE: "Active member",
  REJECTED: "Rejected",
  LAPSED: "Lapsed",
};

export default function AdminApplicationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [app, setApp] = useState<Detail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState<"approve" | "reject" | "activate" | null>(
    null,
  );

  useEffect(() => {
    let cancelled = false;
    if (!id) return;
    setApp(null);
    setLoadError(null);
    apiFetch<{ application: Detail }>(`/api/applications/${id}`)
      .then((data) => {
        if (!cancelled) setApp(data.application);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setLoadError(
            err instanceof ApiError ? err.message : "Couldn't load that application.",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function act(kind: "approve" | "reject") {
    if (!app) return;
    setActionError(null);
    setActionMessage(null);
    setBusy(kind);
    try {
      await apiFetch(`/api/applications/${app.id}/${kind}`, { method: "POST" });
      setActionMessage(
        kind === "approve"
          ? "Approved. Invite email has been sent."
          : "Application marked rejected.",
      );
      // Refresh status in-place.
      const refreshed = await apiFetch<{ application: Detail }>(
        `/api/applications/${app.id}`,
      );
      setApp(refreshed.application);
    } catch (err) {
      setActionError(
        err instanceof ApiError ? err.message : "Couldn't update application.",
      );
    } finally {
      setBusy(null);
    }
  }

  async function activate() {
    if (!app) return;
    setActionError(null);
    setActionMessage(null);
    setBusy("activate");
    try {
      await apiFetch(`/api/members/${app.user.id}/activate`, { method: "POST" });
      setActionMessage("Marked active. They can now use the portal.");
      const refreshed = await apiFetch<{ application: Detail }>(
        `/api/applications/${app.id}`,
      );
      setApp(refreshed.application);
    } catch (err) {
      setActionError(
        err instanceof ApiError ? err.message : "Couldn't activate.",
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <Header />
      <main className="container section" style={{ maxWidth: 760 }}>
        <Link
          to="/admin/applications"
          className="eyebrow"
          style={{ color: "var(--c-palm)" }}
        >
          ← Back to applications
        </Link>

        {loadError && (
          <FormMessage tone="error">
            {loadError}{" "}
            <button
              type="button"
              onClick={() => navigate(0)}
              style={{
                background: "none",
                color: "inherit",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              Retry
            </button>
          </FormMessage>
        )}

        {!app ? (
          !loadError && <p className="eyebrow">Loading…</p>
        ) : (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                gap: "1.25rem",
                alignItems: "center",
                marginTop: "1.25rem",
                marginBottom: "1.5rem",
              }}
            >
              {app.photoUrl ? (
                <img
                  src={app.photoUrl}
                  alt=""
                  width={88}
                  height={88}
                  style={{
                    borderRadius: "50%",
                    objectFit: "cover",
                    boxShadow: "var(--shadow-soft)",
                  }}
                />
              ) : (
                <div
                  aria-hidden="true"
                  style={{
                    width: 88,
                    height: 88,
                    borderRadius: "50%",
                    background: "var(--c-blush)",
                  }}
                />
              )}
              <div>
                <h1
                  className="display"
                  style={{
                    fontSize: "clamp(1.8rem, 4vw, 2.5rem)",
                    margin: 0,
                    lineHeight: 1.05,
                  }}
                >
                  {(app.preferredName ?? app.fullName).toLowerCase()}.
                </h1>
                <div
                  style={{ fontSize: "0.9rem", opacity: 0.75, marginTop: "0.35rem" }}
                >
                  {app.fullName} · {app.email} · {LocationLabels[app.location]}
                </div>
                <div
                  className="eyebrow"
                  style={{ marginTop: "0.5rem", color: "var(--c-coral)" }}
                >
                  {STATUS_LABELS[app.user.status]}
                  {app.reviewedAt && (
                    <span style={{ opacity: 0.7 }}>
                      {" "}
                      · {new Date(app.reviewedAt).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {actionError && <FormMessage tone="error">{actionError}</FormMessage>}
            {actionMessage && (
              <FormMessage tone="success">{actionMessage}</FormMessage>
            )}

            <Card title="Reading life">
              <Row label="Currently reading" value={app.currentRead} />
              <Row
                label="Favorite genres"
                value={
                  app.favoriteGenres.length
                    ? app.favoriteGenres.map((g) => GenreLabels[g]).join(", ")
                    : null
                }
              />
              <Row label="Books per month" value={app.booksPerMonth} />
              <Row label="Book suggestion" value={app.bookSuggestion} />
            </Card>

            <Card title="A little about them">
              <Row label="Why they're joining" value={app.whyJoin} wrap />
              <Row label="How they heard about us" value={app.referralSource} />
              <Row label="Instagram" value={app.instagramHandle} />
            </Card>

            <Card title="Fine print">
              <Row
                label="Acknowledged fee"
                value={app.acknowledgedFee ? "Yes" : "No"}
              />
              <Row
                label="Agreed to guidelines"
                value={app.agreedToGuidelines ? "Yes" : "No"}
              />
              <Row
                label="Submitted"
                value={new Date(app.createdAt).toLocaleString()}
              />
              {app.reviewedBy && (
                <Row
                  label="Reviewed by"
                  value={`${app.reviewedBy.fullName} (${app.reviewedBy.email})`}
                />
              )}
            </Card>

            {app.user.status === "PENDING" && (
              <div
                style={{
                  display: "flex",
                  gap: "0.85rem",
                  flexWrap: "wrap",
                  marginTop: "1.5rem",
                }}
              >
                <button
                  type="button"
                  onClick={() => act("approve")}
                  className="btn btn--primary"
                  disabled={busy !== null}
                >
                  {busy === "approve" ? "Approving…" : "Approve & send invite"}
                </button>
                <button
                  type="button"
                  onClick={() => act("reject")}
                  className="btn btn--ghost"
                  disabled={busy !== null}
                >
                  {busy === "reject" ? "Rejecting…" : "Reject"}
                </button>
              </div>
            )}

            {(app.user.status === "APPROVED_UNPAID" ||
              app.user.status === "LAPSED") && (
              <div
                style={{
                  display: "flex",
                  gap: "0.85rem",
                  flexWrap: "wrap",
                  marginTop: "1.5rem",
                  alignItems: "center",
                }}
              >
                <button
                  type="button"
                  onClick={activate}
                  className="btn btn--primary"
                  disabled={busy !== null}
                >
                  {busy === "activate"
                    ? "Activating…"
                    : "Mark active (skip payment)"}
                </button>
                <span
                  style={{
                    fontSize: "0.85rem",
                    color: "var(--c-palm)",
                    opacity: 0.7,
                  }}
                >
                  Use for comps or before Stripe is wired up.
                </span>
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section
      style={{
        background: "#fff",
        border: "1px solid var(--c-rule)",
        borderRadius: "var(--r-md)",
        padding: "1.25rem 1.4rem",
        marginBottom: "1rem",
      }}
    >
      <h2
        className="eyebrow"
        style={{ color: "var(--c-palm)", marginTop: 0, marginBottom: "0.85rem" }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

function Row({
  label,
  value,
  wrap = false,
}: {
  label: string;
  value: string | null;
  wrap?: boolean;
}) {
  return (
    <div className="kv-row">
      <div className="eyebrow" style={{ color: "var(--c-palm)", opacity: 0.7 }}>
        {label}
      </div>
      <div
        style={{
          whiteSpace: wrap ? "pre-wrap" : "normal",
          color: value ? "var(--c-ink)" : "var(--c-palm)",
          opacity: value ? 1 : 0.5,
        }}
      >
        {value ?? "—"}
      </div>
    </div>
  );
}
